import React, { createContext, useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { Button, Card, CardBody, CardHeader, Col, Form, FormGroup, FormText, Input, Row } from 'reactstrap'

import Email from '../../main/FormFields/Email'
import CharCheckModal from '../../components/molecules/CharCheckModal'
import GPTEstimateModal from '../../components/molecules/GPTEstimateModal'
import { ToastContainer } from 'react-toastify'
import { notify } from '../../utils/notify'
import FileInput from '../../components/molecules/FileInput'
import ClienteSelect from '../../components/molecules/ClienteSelect'
import { useFileUpload } from '../../hooks/useFileUpload'
import { api } from '../../utils'

const EmailContext = createContext()

const LANGUAGES = [
  'it-it',
  'de-de',
  'en-uk',
  'en-us',
  'es-es',
  'fr-fr',
  'en-ca',
  'fr-ca',
  'pl-pl',
  'zh-sg',
  'zh-tw',
  'tr-tr',
  'ja-jp',
  'ar-ae',
  'ar-kw',
  'ko-kr',
  'ar-qa',
  'ar-sa',
  'nl-be',
  'pt-br',
  'nl-nl',
  'no-no',
  'pt-pt',
  'da-dk',
  'fi-fi',
  'el-gr',
  'sv-se',
  'cs-cz',
  'ms-my',
  'zh-hk'
]

const DuplicateKeywordsGptForm = () => {
  const [file, setFile] = useState(null)
  const [emails, setEmails] = useState([])
  const [lingua, setLingua] = useState('it-it')
  const [volume, setVolume] = useState('')
  const { upload, loading } = useFileUpload()
  const [clienti, setClienti] = useState([])
  const [selectedClientId, setSelectedClientId] = useState('')

  // Modal check caratteri
  const [showCharCheck, setShowCharCheck] = useState(false)
  const [errCharList, setErrCharList] = useState([])
  const [acceptedCharCheck, setAcceptedCharCheck] = useState(false)
  // Modal check gpt
  const [showGptCheck, setShowGptCheck] = useState(false)
  const [gptEstimateParams, setGptEstimateParams] = useState({
    n_keywords: -1,
    total_tokens: -1,
    estimate: -1,
  })
  const [acceptedGptCheck, setAcceptedGptCheck] = useState(false)

  const handleSubmit = async event => {
    if (event)
      event.preventDefault()

    if (file == null || lingua === '') {
      notify('File o lingua mancanti', 'error')
      return
    }
    if (!selectedClientId) {
      notify('Seleziona un cliente.', 'error')
      return
    }

    const formData = new FormData()
    formData.append('cliente', selectedClientId)
    formData.append('emails', emails)
    formData.append('file', file)
    formData.append('volume', volume)
    formData.append('lingua', lingua)
    formData.append('gpt_estimate_accepted', acceptedGptCheck)
    formData.append('keyword_error_accepted', acceptedCharCheck)

    try {
      // The hook handles loading, success/error notifications, and the API call.
      await upload('post', 'duplicate-keywords-gpt/', formData, {
        onSuccess: (data) => {
          notify('Richiesta mandata. Riceverai una mail al termine dell\'elaborazione', 'success')
          setAcceptedCharCheck(false)
          setAcceptedGptCheck(false)
          setFile(null)
          setSelectedClientId('')
        },
        onError: (errorMessage, err) => {
          if (err.response?.data?.message?.includes('GPT Estimate')) {
            setGptEstimateParams(err.response.data.estimates)
            setShowGptCheck(true)
          } else if (err.response?.data?.message?.includes('ERROR Encoding keywords')) {
            setErrCharList(err.response.data.errors)
            setShowCharCheck(true)
          } else {
            notify(errorMessage, 'error')

            // Reset the form.
            setFile(null)
            setAcceptedCharCheck(false)
            setAcceptedGptCheck(false)
          }
        },
      })
    } catch (err) {
      console.error('Duplicate Keywords GPT submission failed:', err)
    }
  }

  const confirmEvaluation = (e) => {
    e.preventDefault()
    setShowCharCheck(false)
    setAcceptedCharCheck(true)
  }

  const cancelEvaluation = () => {
    setShowCharCheck(false)
    setAcceptedGptCheck(false)
  }

  const confirmEstimate = (e) => {
    e.preventDefault()
    setShowGptCheck(false)
    setAcceptedGptCheck(true)
  }

  const cancelEstimate = () => {
    setShowGptCheck(false)
    setShowCharCheck(false)
  }

  useEffect(() => {
    api
      .get('clienti/')
      .then((response) => {
        setClienti(Array.isArray(response.data) ? response.data : response.data?.results || [])
      })
      .catch((err) => {
        console.error('Failed to fetch clienti', err)
      })
  }, [])

  useEffect(() => {
    if (acceptedCharCheck || acceptedGptCheck) {
      handleSubmit()
    }
  }, [acceptedCharCheck, acceptedGptCheck])

  return (
    <>
      <Row>
        <div className="col pb-5">
          <Card className="bg-secondary shadow">
            <CardHeader className="bg-white border-0">
              <Row style={{ marginLeft: '0px' }}>
                <Col md={10}>
                  <h1 className="mb-0">Duplicate Keywords</h1>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <CharCheckModal
                show={showCharCheck}
                onHide={cancelEvaluation}
                onSubmit={confirmEvaluation}
                errCharList={errCharList}
                withExclusion={true}
              />
              <GPTEstimateModal
                show={showGptCheck}
                onHide={cancelEstimate}
                onSubmit={confirmEstimate}
                params={gptEstimateParams}
              />
              {loading ? (
                <Spinner animation="grow" />
              ) : (
                <Form onSubmit={handleSubmit} id="idea-form" action="">
                  <Row>
                    <Col md={6}>
                      <ClienteSelect
                        id="duplicate-keywords-cliente"
                        label="Cliente"
                        clienti={clienti}
                        selectedClientId={selectedClientId}
                        onChange={setSelectedClientId}
                        isRequired
                        showCreateLink
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <FileInput
                        onFileChange={setFile}
                        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                        helpText={
                          <>
                            Carica un file contenente le tue keywords da verificare. <br />
                            <b>Il file deve contenere le colonne "Keyword" e "Volume"</b>
                          </>
                        }
                        required={true}
                      />
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <EmailContext.Provider value={{ emails, setEmails }}>
                          <Email context={EmailContext} />
                        </EmailContext.Provider>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <label htmlFor="lingua" style={{ display: 'block' }}>
                          Lingua
                        </label>
                        <Input
                          type="select"
                          className="form-control-alternative"
                          id="lingua"
                          value={lingua}
                          required
                          style={{ height: '44px' }}
                          onChange={event => {
                            setLingua(event.target.value)
                          }}
                        >
                          {LANGUAGES.map(lang => {
                            return (<option key={lang} value={lang}>{lang}</option>)
                          })}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <label htmlFor="volume" className="form-control-label">
                          Volume
                        </label>
                        <Input
                          className="form-control-alternative"
                          type="text"
                          id="volume"
                          value={volume}
                          placeholder="Inserisci volume"
                          onChange={e => setVolume(e.target.value)}
                          required
                        />
                        <FormText color="muted" align={'left'}>
                          Inserisci il nome della colonna volume del foglio excel
                        </FormText>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row style={{ display: 'flex', justifyContent: 'flex-end', marginRight: '50px' }}>
                    <Button color="info" size="lg" onClick={handleSubmit}>Submit Report</Button>
                  </Row>
                </Form>
              )}
              <ToastContainer />
            </CardBody>
          </Card>
        </div>
      </Row>
    </>
  )

}

export default DuplicateKeywordsGptForm
